import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Chip,
  Pagination,
} from '@mui/material';
import { Search, FilterList, Schedule } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Course, Category } from '../types';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import Breadcrumb from '../components/Common/Breadcrumb';

const PAGE_SIZE = 12;

const Courses: React.FC = () => {
  const navigate = useNavigate();
  
  const [courses, setCourses] = useState<Course[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | ''>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const fetchCourses = useCallback(async () => {
    try {
      setLoading(true);
      const coursesResp = await apiService.getPublishedCourses(currentPage, PAGE_SIZE, {
        search: appliedSearch || undefined,
        category: selectedCategory !== '' ? selectedCategory : undefined,
      });
      setCourses(coursesResp.results);
      setTotalCount(coursesResp.count);
      setTotalPages(coursesResp.total_pages);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, appliedSearch, selectedCategory]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  useEffect(() => {
    apiService.getCategories().then(setCategories).catch(() => {});
  }, []);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedSearch(searchTerm);
    setCurrentPage(1);
  };

  const handleCategoryChange = (value: number | '') => {
    setSelectedCategory(value);
    setCurrentPage(1);
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return <LoadingSpinner message="Loading courses..." />;
  }

  return (
    <Box sx={{ py: 8 }}>
      <Container maxWidth="lg">
        {/* Breadcrumb */}
        <Breadcrumb />
        
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #10b981 0%, #22c55e 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Learn Today. Build Your Career Tomorrow
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            Discover practical knowledge, real-world skills, and opportunities that shape your future
          </Typography>
        </Box>

        {/* Search and Filter Section */}
        <Box sx={{ mb: 6 }}>
          <Card sx={{ p: 4, borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
            <Stack
              component="form"
              onSubmit={handleSearchSubmit}
              direction={{ xs: 'column', md: 'row' }}
              spacing={3}
              alignItems="center"
            >
              <Box sx={{ flex: 1, minWidth: '250px' }}>
                <TextField
                  fullWidth
                  placeholder="Search courses..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: '#10b981' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#10b981',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#10b981',
                      },
                    },
                  }}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: '250px' }}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={selectedCategory}
                    label="Category"
                    onChange={(e) => handleCategoryChange(e.target.value as number | '')}
                    startAdornment={<FilterList sx={{ color: '#10b981', mr: 1 }} />}
                    sx={{
                      borderRadius: 2,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0,0,0,0.23)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#10b981',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#10b981',
                      },
                    }}
                  >
                    <MenuItem value="">All Categories</MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Stack>
          </Card>
        </Box>

        {/* Results count and Courses Grid */}
        {totalCount > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {totalCount} course{totalCount !== 1 ? 's' : ''} found
          </Typography>
        )}

        {courses.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
            {courses.map((course) => (
              <Box key={course.id} sx={{ flex: '1 1 350px', maxWidth: '400px' }}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: course.coming_soon ? 'default' : 'pointer',
                    transition: 'all 0.3s ease',
                    border: course.coming_soon 
                      ? '2px solid rgba(251, 191, 36, 0.5)'
                      : '1px solid rgba(0,0,0,0.08)',
                    position: 'relative',
                    overflow: 'hidden',
                    ...(!course.coming_soon && {
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                      },
                    }),
                  }}
                  onClick={() => !course.coming_soon && navigate(`/courses/${course.hashed}`)}
                >
                  {/* Card Image */}
                  <Box sx={{ position: 'relative' }}>
                    <CardMedia
                      component="img"
                      height="200"
                      image={course.image}
                      alt={course.name}
                      sx={{ 
                        objectFit: 'cover',
                        filter: course.coming_soon ? 'grayscale(30%) brightness(0.9)' : 'none',
                      }}
                    />
                    
                      {/* Coming Soon Overlay */}
                      {course.coming_soon && (
                        <>
                          {/* Semi-transparent overlay */}
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(245, 158, 11, 0.15) 100%)',
                              backdropFilter: 'blur(1px)',
                            }}
                          />
                          
                          {/* Coming Soon Badge - Top Right */}
                          <Chip
                            icon={<Schedule />}
                            label="COMING SOON"
                            sx={{
                              position: 'absolute',
                              top: 12,
                              right: 12,
                              background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                              color: '#fff',
                              fontWeight: 700,
                              fontSize: '0.75rem',
                              letterSpacing: '0.5px',
                              boxShadow: '0 4px 20px rgba(251, 191, 36, 0.4)',
                              animation: 'pulse 2s ease-in-out infinite',
                              '@keyframes pulse': {
                                '0%, 100%': {
                                  boxShadow: '0 4px 20px rgba(251, 191, 36, 0.4)',
                                },
                                '50%': {
                                  boxShadow: '0 4px 30px rgba(251, 191, 36, 0.8)',
                                },
                              },
                            }}
                          />
                        </>
                      )}
                  </Box>
                  
                  {/* Card Content */}
                  <CardContent sx={{ flexGrow: 1, p: 2, pt: 0.75, pb: '0 !important' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: '36px' }}>
                      <Typography 
                        variant="body1" 
                        component="h3" 
                        sx={{ 
                          fontWeight: 600, 
                          fontSize: '0.9rem', 
                          lineHeight: 1.1, 
                          flex: 1,
                          pr: 1,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          opacity: course.coming_soon ? 0.9 : 1,
                        }}
                      >
                        {course.name}
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          fontWeight: 700, 
                          color: course.coming_soon ? '#fbbf24' : '#10b981', 
                          fontSize: '1rem',
                          whiteSpace: 'nowrap',
                          flexShrink: 0
                        }}
                      >
                        {course.is_free ? 'Free' : `$${course.price}`}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        ) : null}

        {!loading && courses.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h5" color="text.secondary" gutterBottom>
              No courses found
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Try adjusting your search criteria or browse all categories.
            </Typography>
          </Box>
        )}

        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default Courses;